import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarErrorBoundary } from '@/components/booking/CalendarErrorBoundary';
import { SharedLeftColumn } from './SharedLeftColumn';
import { DatePickerStep } from '../steps/DatePickerStep';
import { TimeSlotStep } from '../steps/TimeSlotStep';
import { ContactFormStep } from '../steps/ContactFormStep';
import { MarketingFooter } from './MarketingFooter';
import { ModernTemplateBackground } from './ModernTemplateBackground';
import { DEFAULT_BOOKING_SETTINGS } from '@/lib/constants/booking-defaults';
import { cn } from '@/lib/utils';
import { getDateKeyInTimezone } from '@/lib/timezone/date-utils';
import { format } from 'date-fns';
import { DateTimeDisplay } from './DateTimeDisplay';
import { BookingManagementActions } from '@/components/booking/actions/BookingManagementActions';
import { useBookingContext } from '@/contexts/BookingContext';

// Helper function to get location display text based on location_type
const getLocationDisplayText = (locationType?: string): string => {
  switch (locationType) {
    case 'google_meet':
      return 'Google Meet';
    case 'zoom':
      return 'Zoom Meeting';
    case 'phone_call':
    case 'attendee_phone':
    case 'organizer_phone':
      return 'Phone Call';
    case 'in_person':
    case 'attendee_address':
    case 'organizer_address':
      return 'In-Person';
    case 'custom':
      return 'Custom Location';
    default:
      return 'Google Meet'; // Default fallback
  }
};

interface DesktopBookingLayoutProps {
  // Template data
  meetingLink: any;
  fontClass: string;
  themeCustomizations: any;
  
  // UI state
  step: string;
  selectedDate?: Date;
  selectedTime?: string;
  selectedTimezone: string;
  
  // Handlers
  handleBack: () => void;
  handleDateSelected: (date: Date) => void;
  handleTimeSelected: (time: string) => void;
  setSelectedTimezone: (timezone: string) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
  
  // Form data
  formData: any;
  customAnswers: any;
  guests: any[];
  handleInputChange: any;
  handleCustomAnswerChange: any;
  setGuests: any;
  customQuestions: any[];
  
  // Calendar data
  availableSlots: any;
  isDayAvailable: (date: Date) => boolean;
  slotsLoading: boolean;
  shouldShowSkeleton: boolean;
  organizerTimezone: string;
  
  // Navigation
  handlePrevDay: () => void;
  handleNextDay: () => void;
  
  // Settings
  debugSettings: any;
  troubleshootMode: boolean;
  
  // Submission
  loading: boolean;
  error: any;
  
  // Enhanced time slot props
  showAllSlots?: boolean;
  enhancedAvailableSlots?: Record<string, { preferred: string[]; available: string[] }>;
  energyPreference?: string;
}

export const DesktopBookingLayout: React.FC<DesktopBookingLayoutProps> = ({
  meetingLink,
  fontClass,
  themeCustomizations,
  step,
  selectedDate,
  selectedTime,
  selectedTimezone,
  energyPreference,
  handleBack,
  handleDateSelected,
  handleTimeSelected,
  setSelectedTimezone,
  handleFormSubmit,
  formData,
  customAnswers,
  guests,
  handleInputChange,
  handleCustomAnswerChange,
  setGuests,
  customQuestions,
  availableSlots,
  isDayAvailable,
  slotsLoading,
  shouldShowSkeleton,
  organizerTimezone,
  handlePrevDay,
  handleNextDay,
  debugSettings,
  troubleshootMode,
  loading,
  error,
  showAllSlots = false,
  enhancedAvailableSlots
}) => {
  // Determine layout based on step
  const isThreeColumn = step === "timeslot";
  const showMiddleColumn = step === "date" || step === "timeslot";
  const showRightColumn = step === "timeslot" || step === "contact";
  // Get booking context for reschedule mode (optional - only if BookingProvider exists)
  let isRescheduleMode = false;
  let isCancelMode = false;
  
  try {
    const bookingContext = useBookingContext();
    isRescheduleMode = bookingContext.isRescheduleMode;
    isCancelMode = bookingContext.isCancelMode;
  } catch (error) {
    // BookingProvider not found - this is fine for preview/edit pages
  }
  const logoToUse = themeCustomizations?.customLogoUrl || meetingLink.profiles?.business_logo_url;
  const businessName = meetingLink.profiles?.business_name || DEFAULT_BOOKING_SETTINGS.BUSINESS_NAME;

  // Get available slots for the selected date
  const currentAvailableSlots = useMemo(() => {
    if (!selectedDate) return [];
    
    if (typeof availableSlots === 'object' && !Array.isArray(availableSlots)) {
      const dateKey = getDateKeyInTimezone(selectedDate, organizerTimezone);
      return availableSlots[dateKey] || [];
    }
    
    return Array.isArray(availableSlots) ? availableSlots : [];
  }, [selectedDate, availableSlots, organizerTimezone]);

  // Handler to go back to date step
  const handleBackToDateStep = useCallback(() => {
    if (step === 'timeslot' || step === 'contact') {
      handleBack(); // This should set step to 'date' in parent logic
    }
  }, [step, handleBack]);

  // Check if the selected time is an optimal slot
  const isOptimalSlot = useMemo(() => {
    if (!selectedTime || !selectedDate || !enhancedAvailableSlots) return false;
    
    const dateKey = getDateKeyInTimezone(selectedDate, organizerTimezone);
    const daySlots = enhancedAvailableSlots[dateKey];
    
    if (!daySlots) return false;
    
    // Check if the selected time is in the preferred slots
    return daySlots.preferred.includes(selectedTime);
  }, [selectedTime, selectedDate, enhancedAvailableSlots, organizerTimezone]);

  return (
    <CalendarErrorBoundary>
      <ModernTemplateBackground themeCustomizations={themeCustomizations}>
        <div className={cn("h-screen flex flex-col", fontClass)} data-testid="booking-page">
          <div className="flex-1 flex items-center justify-center p-4 min-h-0">
            <div className="w-full h-full min-h-0">
              <Card className="mx-auto w-full max-w-6xl border-none shadow-2xl overflow-hidden h-full">
                <CardContent className="p-0 h-full">
                  <div className="flex gap-0 h-full transition-all duration-75 ease-out min-h-0">
                    
                    {/* Start Left Col */}
                    <div 
                      className="transition-all duration-75 ease-out flex-shrink-0"
                        style={{
                          width: isThreeColumn ? '33.333%' : '50%',
                          transition: 'width 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                        }}
                    >
                      <SharedLeftColumn
                        logoUrl={logoToUse}
                        businessName={businessName}
                        meetingName={meetingLink.name}
                        description={meetingLink.description}
                        duration={debugSettings.duration}
                        timezone={selectedTimezone}
                        location={getLocationDisplayText(meetingLink.location_type)}
                        logoBorderRadius={themeCustomizations?.logoBorderRadius}
                        logoSize={themeCustomizations?.logoSize}
                        isLogoRounded={meetingLink.is_logo_rounded || themeCustomizations?.isLogoRounded}
                        customizations={themeCustomizations}
                        selectedDate={selectedDate}
                        selectedTime={selectedTime}
                        organizerTimezone={organizerTimezone}
                        onTimezoneChange={setSelectedTimezone}
                        onBackToDateStep={(step === 'timeslot' || step === 'contact') ? handleBackToDateStep : undefined}
                        isOptimalSlot={isOptimalSlot}
                      />
                    </div>
                    {/* End Left Col */}


                    {/* Middle Col - Calendar */}
                    {showMiddleColumn && (
                      <div 
                        className="p-6 pt-4 bg-white overflow-y-auto h-full border-r border-zinc-100 transition-all duration-75 ease-out flex-shrink-0 flex flex-col"
                        style={{
                          width: isThreeColumn ? '33.333%' : '50%',
                          transition: 'width 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 100ms ease-in',
                          opacity: showMiddleColumn ? 1 : 0
                        }}
                      >
                        <DatePickerStep
                          selectedDate={selectedDate}
                          onDateSelect={handleDateSelected}
                          isDayAvailable={isDayAvailable}
                          availableSlots={currentAvailableSlots as string[]}
                          hostUserId={meetingLink.user_id}
                          troubleshootMode={troubleshootMode}
                          loading={slotsLoading}
                          shouldShowSkeleton={shouldShowSkeleton}
                        />
                      </div>
                    )}

                    {/* Right Col - Time Slots or Contact Form */}
                    {showRightColumn && (
                      <div 
                        className="p-6 pt-4 bg-white h-full transition-all duration-75 ease-out flex-shrink-0 flex flex-col min-h-0"
                        style={{
                          width: isThreeColumn ? '33.333%' : '50%',
                          transition: 'width 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 100ms ease-in',
                          opacity: showRightColumn ? 1 : 0
                        }}
                      >
                      {selectedDate && step === "timeslot" && selectedDate && (
                        <div className="flex-1 min-h-0 flex flex-col">
                          <TimeSlotStep
                            selectedTimezone={selectedTimezone}
                            availableSlots={currentAvailableSlots as string[]}
                            onTimeSelected={handleTimeSelected}
                            selectedDate={selectedDate}
                            onPrevDay={handlePrevDay}
                            onNextDay={handleNextDay}
                            troubleshootMode={troubleshootMode}
                            loading={slotsLoading}
                            shouldShowSkeleton={shouldShowSkeleton}
                            organizerTimezone={organizerTimezone}
                            showAllSlots={showAllSlots}
                            enhancedAvailableSlots={enhancedAvailableSlots}
                            meetingLinkId={meetingLink.id}
                            organizerUserId={meetingLink.user_id}
                            energyPreference={energyPreference}
                          />
                          
                          {/* Step navigation at the bottom */}
                          <div className={cn("flex sm:hidden w-full items-center justify-between pt-6 pb-2 flex-shrink-0")}>
                            <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-button">
                              <ArrowLeft className="h-4 w-4 mr-2" />
                              Back
                            </Button>
                            <div className="text-sm text-gray-500 justify-end">
                              Step 2 of 3
                            </div>
                          </div>
                        </div>
                      )}

                      {step === "contact" && (
                        <div className="flex-1 min-h-0">
                          <CalendarErrorBoundary>
                            {(isRescheduleMode || isCancelMode) ? (
                              <BookingManagementActions 
                                className="mt-6" 
                                onBackToDateSelection={handleBack}
                              />
                            ) : (
                              <ContactFormStep
                                formData={formData}
                                customAnswers={customAnswers}
                                guests={guests}
                                onInputChange={handleInputChange}
                                onCustomAnswerChange={handleCustomAnswerChange}
                                onGuestsChange={setGuests}
                                onSubmit={handleFormSubmit}
                                onBack={handleBack}
                                customQuestions={customQuestions}
                                loading={loading}
                                error={error}
                                isRescheduleMode={isRescheduleMode}
                                isCancelMode={isCancelMode}
                              />
                            )}
                          </CalendarErrorBoundary>
                        </div>
                      )}
                      </div>
                    )}
                    
                    {/* End Right Col */}

                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <MarketingFooter
            meetingLinkId={meetingLink.id}
            backgroundUrl={themeCustomizations?.backgroundColor || themeCustomizations?.backgroundImageUrl}
          />
        </div>
      </ModernTemplateBackground>
    </CalendarErrorBoundary>
  );
};
